import { useEffect, useRef } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import type { StopMapItem } from '@mbus/shared';
import type { VehicleMapItem } from '../api';
import { minutesLabel } from '../format';

export interface MapBounds {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

interface Props {
  stops: StopMapItem[];
  vehicles: VehicleMapItem[];
  userLoc: { lat: number; lon: number } | null;
  flyTo: { lat: number; lon: number; zoom?: number } | null;
  selectedStopUid: string | null;
  tripShape: [number, number][] | null;
  onSelectStop: (stopUid: string, name: string) => void;
  onMoveEnd: (bounds: MapBounds, center: { lat: number; lon: number }, zoom: number) => void;
}

/** 国土地理院 淡色地図タイル。出典表示は必須。 */
const GSI_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  sources: {
    gsi: {
      type: 'raster',
      tiles: ['https://cyberjapandata.gsi.go.jp/xyz/pale/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 18,
      attribution: '<a href="https://maps.gsi.go.jp/development/ichiran.html" target="_blank" rel="noopener">国土地理院</a>',
    },
  },
  layers: [
    { id: 'bg', type: 'background', paint: { 'background-color': '#e8ecef' } },
    { id: 'gsi', type: 'raster', source: 'gsi', paint: { 'raster-saturation': -0.35, 'raster-brightness-min': 0.05 } },
  ],
};

const QUALITY_COLOR: Record<string, string> = {
  realtime: '#0a8f4c',
  predicted: '#c77700',
  scheduled: '#5a6472',
  unknown: '#8a93a0',
};

function stopsToGeoJSON(stops: StopMapItem[], selected: string | null): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: stops.map((s) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lon, s.lat] },
      properties: {
        uid: s.stopUid,
        name: s.name,
        label: s.next ? minutesLabel(s.next.minutes) : '',
        headsign: s.next?.headsign ?? '',
        color: s.next ? QUALITY_COLOR[s.next.quality] ?? QUALITY_COLOR.unknown : '#aab2bc',
        hasNext: s.next ? 1 : 0,
        selected: s.stopUid === selected ? 1 : 0,
      },
    })),
  };
}

function vehiclesToGeoJSON(vehicles: VehicleMapItem[]): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: vehicles.map((v) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [v.lon, v.lat] },
      properties: { id: v.vehicleId, bearing: v.bearing ?? 0, hasBearing: v.bearing === null ? 0 : 1 },
    })),
  };
}

const EMPTY_FC: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

export function MapView(props: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const readyRef = useRef(false);
  const propsRef = useRef(props);
  propsRef.current = props;

  // ---- 初期化 (一度だけ) -----------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: GSI_STYLE,
      center: [139.767125, 35.681236],
      zoom: 15,
      minZoom: 5,
      maxZoom: 18.5,
      // 地図タイル=国土地理院。一部のバス停位置は OSM 由来のため ODbL 出典表示が必須。
      // バス時刻・停留所データは各事業者/自治体のオープンデータ (CC BY 4.0 等) — 名古屋市交通局は個別クレジット必須。
      attributionControl: {
        compact: true,
        customAttribution:
          'バスデータ: 各事業者・<a href="https://data.bodik.jp/dataset/231002_7109030000_bus-gtfs-jp" target="_blank" rel="noopener">名古屋市交通局</a>ほか (CC BY 4.0 等) / 一部停留所位置 <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">© OpenStreetMap contributors</a>',
      },
    });
    mapRef.current = map;

    map.on('load', () => {
      // --- ソース ---
      map.addSource('stops', { type: 'geojson', data: EMPTY_FC });
      map.addSource('vehicles', { type: 'geojson', data: EMPTY_FC });
      map.addSource('user', { type: 'geojson', data: EMPTY_FC });
      map.addSource('trip-shape', { type: 'geojson', data: EMPTY_FC });

      // --- 便の形状 (選択時のみ) ---
      map.addLayer({
        id: 'trip-shape-line',
        type: 'line',
        source: 'trip-shape',
        paint: { 'line-color': '#1d4ed8', 'line-width': 4, 'line-opacity': 0.75 },
      });

      // --- 停留所: 丸 ---
      map.addLayer({
        id: 'stops-circle',
        type: 'circle',
        source: 'stops',
        paint: {
          'circle-radius': [
            'case',
            ['==', ['get', 'selected'], 1], 11,
            ['==', ['get', 'hasNext'], 1], 9,
            5,
          ],
          'circle-color': ['get', 'color'],
          'circle-stroke-width': ['case', ['==', ['get', 'selected'], 1], 3, 2],
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95,
        },
      });

      // --- 停留所: 「N分」バッジ ---
      map.addLayer({
        id: 'stops-minutes',
        type: 'symbol',
        source: 'stops',
        filter: ['==', ['get', 'hasNext'], 1],
        layout: {
          'text-field': ['get', 'label'],
          'text-size': 11,
          'text-offset': [0, -1.6],
          'text-allow-overlap': false,
          'text-font': ['Noto Sans Regular'],
        },
        paint: {
          'text-color': '#0b1220',
          'text-halo-color': '#ffffff',
          'text-halo-width': 2,
        },
      });

      // --- 停留所名 (ズーム時のみ) ---
      map.addLayer({
        id: 'stops-name',
        type: 'symbol',
        source: 'stops',
        minzoom: 15.5,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 10,
          'text-offset': [0, 1.4],
          'text-optional': true,
          'text-font': ['Noto Sans Regular'],
        },
        paint: {
          'text-color': '#334155',
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5,
        },
      });

      // --- 車両 (リアルタイム位置) ---
      map.addLayer({
        id: 'vehicles-dot',
        type: 'circle',
        source: 'vehicles',
        paint: {
          'circle-radius': 7,
          'circle-color': '#1d4ed8',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
        },
      });
      map.addLayer({
        id: 'vehicles-arrow',
        type: 'symbol',
        source: 'vehicles',
        filter: ['==', ['get', 'hasBearing'], 1],
        layout: {
          'text-field': '▲',
          'text-size': 9,
          'text-rotate': ['get', 'bearing'],
          'text-rotation-alignment': 'map',
          'text-allow-overlap': true,
          'text-font': ['Noto Sans Regular'],
        },
        paint: { 'text-color': '#ffffff' },
      });

      // --- 現在地 ---
      map.addLayer({
        id: 'user-dot',
        type: 'circle',
        source: 'user',
        paint: {
          'circle-radius': 8,
          'circle-color': '#2563eb',
          'circle-stroke-width': 3,
          'circle-stroke-color': '#ffffff',
        },
      });

      // --- 操作 ---
      map.on('click', 'stops-circle', (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const uid = f.properties?.uid as string;
        const name = f.properties?.name as string;
        propsRef.current.onSelectStop(uid, name);
      });
      map.on('mouseenter', 'stops-circle', () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', 'stops-circle', () => {
        map.getCanvas().style.cursor = '';
      });

      readyRef.current = true;
      // 初期データ反映
      syncAll();
      emitMoveEnd();
    });

    const emitMoveEnd = () => {
      const b = map.getBounds();
      const c = map.getCenter();
      propsRef.current.onMoveEnd(
        { minLat: b.getSouth(), minLon: b.getWest(), maxLat: b.getNorth(), maxLon: b.getEast() },
        { lat: c.lat, lon: c.lng },
        map.getZoom(),
      );
    };
    map.on('moveend', emitMoveEnd);

    const syncAll = () => {
      const p = propsRef.current;
      (map.getSource('stops') as GeoJSONSource | undefined)?.setData(
        stopsToGeoJSON(p.stops, p.selectedStopUid),
      );
      (map.getSource('vehicles') as GeoJSONSource | undefined)?.setData(vehiclesToGeoJSON(p.vehicles));
      if (p.userLoc) {
        (map.getSource('user') as GeoJSONSource | undefined)?.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [p.userLoc.lon, p.userLoc.lat] },
              properties: {},
            },
          ],
        });
      }
    };

    return () => {
      readyRef.current = false;
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- データ更新 -------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    (map.getSource('stops') as GeoJSONSource | undefined)?.setData(
      stopsToGeoJSON(props.stops, props.selectedStopUid),
    );
  }, [props.stops, props.selectedStopUid]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    (map.getSource('vehicles') as GeoJSONSource | undefined)?.setData(vehiclesToGeoJSON(props.vehicles));
  }, [props.vehicles]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current || !props.userLoc) return;
    (map.getSource('user') as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [props.userLoc.lon, props.userLoc.lat] },
          properties: {},
        },
      ],
    });
  }, [props.userLoc]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !readyRef.current) return;
    const src = map.getSource('trip-shape') as GeoJSONSource | undefined;
    if (!src) return;
    if (props.tripShape && props.tripShape.length >= 2) {
      src.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: props.tripShape },
            properties: {},
          },
        ],
      });
    } else {
      src.setData(EMPTY_FC);
    }
  }, [props.tripShape]);

  // ---- flyTo ------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !props.flyTo) return;
    map.flyTo({
      center: [props.flyTo.lon, props.flyTo.lat],
      zoom: props.flyTo.zoom ?? map.getZoom(),
      duration: 800,
    });
  }, [props.flyTo]);

  return <div ref={containerRef} className="map" />;
}
